import type {CopilotSpace, CopilotSpacePayload, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {announce} from '@github-ui/aria-live'
import {fetchCopilotSpace} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {SpaceIcon} from '@primer/octicons-react'
import {ActionList, SelectPanel, Spinner, type SelectPanelItemInput} from '@primer/react'
import {use, useCallback, useState} from 'react'

import type {AhpSpaceSelection as QuickChatSpaceSelection} from '@github-ui/ahp-session-client/contracts'

import {useComposerTelemetry} from '../telemetry/ComposerTelemetryContext'
import {useSpaces} from './use-spaces'

interface SpacePickerProps {
  selectedSpace: QuickChatSpaceSelection | undefined
  onSelectSpace: (space: QuickChatSpaceSelection | undefined) => void
  isResolving: boolean
  onResolvingChange: (isResolving: boolean) => void
  selectionError: string | undefined
  onSelectionErrorChange: (selectionError: string | undefined) => void
  disabled?: boolean
  disabledDescription?: string
  portalContainerName?: string
}

const NONE_KEY = 'none'

function spaceKey(space: Pick<IndexCopilotSpace, 'owner' | 'id'>): string {
  return `${space.owner}/${space.id}`
}

function isSpacePayload(payload: CopilotSpacePayload): payload is CopilotSpace {
  return 'generalInstructions' in payload
}

/**
 * Renders the searchable space selector inside the composer's attach ("+") menu.
 * Must be rendered as a child of an `ActionList` (e.g. via `ChatComposer`'s
 * `attachMenuContent` prop) so Primer wires up the nested-menu behavior.
 */
export function SpacePicker({
  selectedSpace,
  onSelectSpace,
  isResolving,
  onResolvingChange,
  selectionError,
  onSelectionErrorChange,
  disabled = false,
  disabledDescription,
  portalContainerName,
}: SpacePickerProps) {
  const {afterSelect: closeAttachMenu} = use(ActionList.ContainerContext)
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const telemetry = useComposerTelemetry()
  const {
    data: spaces,
    isLoading,
    isError,
  } = useSpaces({
    enabled: isOpen,
    onError: () => announce('Could not load spaces', {assertive: true}),
  })

  const handleSelect = async (space: IndexCopilotSpace) => {
    if (disabled || isResolving) return
    onSelectionErrorChange(undefined)
    onResolvingChange(true)
    announce(`Resolving space: ${space.name}`)
    try {
      const result = await fetchCopilotSpace({owner: space.owner, id: space.id})
      const payload = result.ok && isSpacePayload(result.payload) ? result.payload : undefined

      if (!payload?.slug) {
        const message =
          result.ok && 'protectedOrganizations' in result.payload
            ? 'Authorize SSO for this space, then try again.'
            : 'Could not select this space. Try again.'
        onSelectionErrorChange(message)
        announce(message, {assertive: true})
        return
      }

      telemetry?.track('space_selected', {replaced_existing: selectedSpace !== undefined})
      onSelectSpace({
        owner: space.owner,
        id: space.id,
        slug: payload.slug,
        name: space.name,
      })
      announce(`Space selected: ${space.name}`)
      setIsOpen(false)
      closeAttachMenu?.()
    } catch {
      const message = 'Could not select this space. Try again.'
      onSelectionErrorChange(message)
      announce(message, {assertive: true})
    } finally {
      onResolvingChange(false)
    }
  }

  const handleClear = useCallback(() => {
    if (disabled || isResolving) return
    onSelectionErrorChange(undefined)
    if (selectedSpace) telemetry?.track('space_cleared', {via: 'space_picker'})
    onSelectSpace(undefined)
  }, [disabled, isResolving, onSelectSpace, onSelectionErrorChange, selectedSpace, telemetry])

  const selectedKey = selectedSpace ? spaceKey(selectedSpace) : NONE_KEY
  const buttonLabel = selectedSpace ? selectedSpace.name : 'No space'
  const isLocked = disabled || isResolving
  const query = filter.trim().toLowerCase()
  // Spaces with private sources outside the owner's scope can't load in a sandbox chat.
  const hideOutOfScope = copilotFeatureFlags.spacesOrgScoping
  const filteredSpaces = spaces?.filter(
    space =>
      !(hideOutOfScope && space.hasOutOfScopePrivateSources) &&
      (space.name.toLowerCase().includes(query) || space.owner.toLowerCase().includes(query)),
  )
  const items: SelectPanelItemInput[] = [
    {id: NONE_KEY, text: 'No space', disabled: isLocked},
    ...(filteredSpaces ?? []).map(
      space =>
        ({
          id: spaceKey(space),
          text: space.name,
          description: space.owner,
          disabled: isLocked,
          onAction: (_item, event) => {
            event.preventDefault()
            void handleSelect(space)
          },
        }) satisfies SelectPanelItemInput,
    ),
  ]
  if (isError || (!isLoading && !filteredSpaces?.length)) {
    items.push({
      id: 'spaces-status',
      text: isError ? 'Could not load spaces' : spaces?.length ? 'No matching spaces' : 'No spaces available',
      disabled: true,
    })
  }

  // `disabled` on the anchor `ActionList.Item` only sets ARIA/visual state — the
  // `SelectPanel` still wires its own click/keyboard handlers, so guard the
  // open transition itself to keep a locked Space selection unreachable.
  const handleOpenChange = (next: boolean) => {
    if (next && isLocked) return
    if (next) setFilter('')
    setIsOpen(next)
  }

  return (
    <SelectPanel
      title="Select a space"
      subtitle="Filter spaces by name or owner"
      inputLabel="Filter spaces by name or owner"
      placeholderText="Find a space"
      open={isOpen}
      onOpenChange={handleOpenChange}
      filterValue={filter}
      onFilterChange={setFilter}
      items={items}
      selected={{id: selectedKey, text: buttonLabel}}
      onSelectedChange={handleClear}
      loading={isLoading}
      showSelectedOptionsFirst={false}
      notice={selectionError ? {variant: 'error', text: selectionError} : undefined}
      overlayProps={{width: 'medium', maxHeight: 'medium', portalContainerName}}
      renderAnchor={props => (
        <ActionList.Item {...props} role="menuitem" disabled={isLocked} aria-haspopup="dialog">
          <ActionList.LeadingVisual>
            {isResolving ? <Spinner size="small" srText={null} /> : <SpaceIcon />}
          </ActionList.LeadingVisual>
          {`Space: ${isResolving ? 'Resolving…' : buttonLabel}`}
          {disabled && disabledDescription && <ActionList.Description>{disabledDescription}</ActionList.Description>}
        </ActionList.Item>
      )}
    />
  )
}
